import {
  listContractMakerLibraryCategoriesQueryOptions,
  listContractMakerTemplatesQueryOptions,
} from "@dashboard/lib/contracts/contract-maker-queries"
import { contractMakerRouteFromLocation } from "@dashboard/routes/dashboard-navigation"
import { LazyDashboardApp } from "@dashboard/routes/lazy-dashboard-app"
import { dashboardAppHostRedirectUrl } from "@dashboard-config/dashboard-host"
import { currentRequestHostname } from "@dashboard-config/dashboard-host-runtime"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { DashboardShellSkeleton } from "@ui-dashboard/DashboardShellSkeleton"
import { Suspense } from "react"

export const Route = createFileRoute("/dashboard/contract-maker/$")({
  component: DashboardContractMakerSplatRoute,
  beforeLoad: async ({ location }) => {
    const redirectHref = dashboardAppHostRedirectUrl(await currentRequestHostname(), location.href)
    if (redirectHref) throw redirect({ href: redirectHref })
  },
  /**
   * This route had no loader, and it is the one that most needed one.
   *
   * Contract Maker pulls the largest chunk in the dashboard, and with nothing
   * started here the sequence was strictly serial: commit, download the chunk,
   * mount, and only then ask for the library. The content sections start their
   * chunk and their data together, which is most of why they feel different.
   *
   * Nothing is awaited in the browser, for the same reason as the section
   * route: a loader that waits holds the old page on screen. The server waits,
   * because there the wait is what puts the rows in the HTML.
   */
  loader: async ({ context, location }) => {
    const chunks =
      typeof window === "undefined"
        ? null
        : (void import("@dashboard/routes/DashboardApp"),
          void import("@dashboard/components/contracts/ContractsWorkspace"),
          null)

    /*
     * Only the library is worth prefetching. The builder and the admin editor
     * open one template by an id this route knows nothing about until the path
     * is parsed, and they fetch it themselves.
     */
    const contractRoute = contractMakerRouteFromLocation({
      pathname: location.pathname,
      search: location.searchStr,
    })
    if (contractRoute.type !== "library") return chunks

    const library = Promise.all([
      context.queryClient.ensureQueryData(listContractMakerTemplatesQueryOptions()),
      context.queryClient.ensureQueryData(listContractMakerLibraryCategoriesQueryOptions()),
    ]).catch(() => undefined)

    if (typeof window === "undefined") await library

    return chunks
  },
})

function DashboardContractMakerSplatRoute() {
  return (
    <Suspense fallback={<DashboardShellSkeleton />}>
      <LazyDashboardApp activeSection="contractMaker" />
    </Suspense>
  )
}
